import { useState, useEffect } from "react";
import { Col, Row } from "react-bootstrap";
import Button from "../../../components/shared/buttons/button";
import DateSelector from "../../../components/shared/fields/DateSelector";
import BoxOrange from "../../../images/box_orange.svg";
import GridCard from "../../../components/shared/gridCard/GridCard";
import NoDataFound from "../../../components/shared/noDataFound/NoDataFound";
import InfoCard from "../../../components/shared/infocard/InfoCard";
import { PlusSvg } from "../../../svgFiles/PlusSvg";
import { useGetCheekStripPackedDataForReworkQuery, useGetReworkPackingSummaryQuery } from "../rework/reworkApi";
import RewarkPackedDataModal from "../rework/RewarkPackedDataModal";
import useTranslator from "../../../hooks/useTranslator";
import { formatDateAndTime, getCurrentDate, makePriorLotNumber } from "../../../helpers/Utils";
import { PROJECT_DATE_FORMAT } from "../../../helpers/constants";
import Box from "../../../images/boxes.svg";
import KgGreen from "../../../images/kg_green.svg";
import PackingBandModal from "../packing/PackingBandModal";
import {
  useGetReworkPackingBandRecordQuery,
  useCreateReworkPackingBandRecordMutation,
  useUpdateReworkPackingBandRecordMutation,
} from "../rework/reworkApi";
const ReworkPacking = () => {
  const { _t } = useTranslator();
  const currentDate = getCurrentDate("YYYY-MM-DD");
  const [date, setDate] = useState(currentDate);
  const [lotNumber, setLotNumber] = useState(makePriorLotNumber(date));
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState("");
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [addPackingData, setAddPackingData] = useState(false);
  const [cheeksBoxesPackedData, setCheeksBoxesPackedData] = useState([]);
  const [stripsBoxesPackedData, setStripsBoxesPackedData] = useState([]);

  // Packing Band states
  const [showNightBandModal, setShowNightBandModal] = useState(false);
  const [nightBandMode, setNightBandMode] = useState("add");
  const [nightBandData, setNightBandData] = useState(null);

  useEffect(() => {
    setLotNumber(makePriorLotNumber(date));
  }, [date]);

  const { data: nightBandRecordData } = useGetReworkPackingBandRecordQuery({
    date,
    shift: "night",
    lot_number: lotNumber,
    data_from: "rework_packing"
  });
  const [createPackingBandRecord] = useCreateReworkPackingBandRecordMutation();
  const [updatePackingBandRecord] = useUpdateReworkPackingBandRecordMutation();

  useEffect(() => { setNightBandData(nightBandRecordData?.data?.id ? nightBandRecordData.data : null); }, [nightBandRecordData]);

  const handleNightBandAdd = () => { setShowNightBandModal(true); setNightBandMode(nightBandData?.id ? "edit" : "add"); };

  const handleNightBandSave = async (data) => {
    try {
      const payload = {
        ...data,
        data_from: "rework_packing"
      };

      if (nightBandData?.id) await updatePackingBandRecord({ id: nightBandData.id, requestBody: payload }).unwrap();
      else await createPackingBandRecord(payload).unwrap();
    } catch (error) { console.error("Error saving night band data:", error); }
  };

  const { data: summaryData } = useGetReworkPackingSummaryQuery(
    { 
      date,
      lot_number : lotNumber
    },
    { refetchOnMountOrArgChange: true }
  );

  const { data: packedData } = useGetCheekStripPackedDataForReworkQuery(
    { date, data_source: "rework" },
    { refetchOnMountOrArgChange: true }
  );

  const isToday = date === currentDate;
  const cheeksRecord = packedData?.data?.data?.find((item) => item.type === "cheeks" && item?.id);
  const stripsRecord = packedData?.data?.data?.find((item) => item.type === "strips" && item?.id);
  const hasCheeksRecord = Boolean(cheeksRecord);
  const hasStripsRecord = Boolean(stripsRecord);
  const hasNightBandRecord = Boolean(nightBandData?.id);

  const nightBandLogData = nightBandData?.id
    ? [1, 2, 3, 4, 5].map((n) => [
        { label: _t("band", "Band") + ` ${n}`, value: nightBandData[`band_${n}_20lb`] || 0, subvalue: "(20 Pound)" },
        { label: _t("band", "Band") + ` ${n}`, value: nightBandData[`band_${n}_10lb`] || 0, subvalue: "(10 Pound)" },
        { label: _t("band", "Band") + ` ${n}`, value: nightBandData[`band_${n}_people`] || 0, subvalue: `(${_t("people", "People")})` },
      ])
    : [];


  const listItems = [
    { label: _t("prime_cheeks_kg", "Prime Cheeks KG"), value: summaryData?.data?.total_no_of_prime_kg_in_cheeks ?? 0, icon: KgGreen },
    { label: _t("prime_strips_kg", "Prime Strips KG"), value: summaryData?.data?.total_no_of_prime_kg_in_strips ?? 0, icon: KgGreen },
    { label: _t("seconds_cheeks_kg", "Second Cheeks KG"), value: summaryData?.data?.seconds_cheeks_kg ?? 0, icon: KgGreen },
    { label: _t("seconds_strips_kg", "Second Strips KG"), value: summaryData?.data?.seconds_strips_kg ?? 0, icon: KgGreen },
    { label: _t("total_tapita_kg", "Total Tapita KG"), value: summaryData?.data?.total_tapita_kg ?? 0, icon: KgGreen },
    { label: _t("total_pieces_kg", "Total Pieces KG"), value: summaryData?.data?.total_pieces_kg ?? 0, icon: KgGreen },
  ];

  const reworkPackingOverviewItems = [
    {
      label: _t("total_boxes", "Total Boxes"),
      value: summaryData?.data?.total_boxes_packed ?? 0,
      icon: BoxOrange,
    },
    {
      label: _t("prime_cheek_boxes_10_pound", "Prime Cheek Boxes (10 pound)"),
      value: summaryData?.data?.total_prime_cheek_boxes_10_pound ?? 0,
      icon: Box,
    },
    {
      label: _t("prime_cheek_boxes_20_pound", "Prime Cheek Boxes (20 pound)"),
      value: summaryData?.data?.total_prime_cheek_boxes_20_pound ?? 0,
      icon: Box,
    },
    {
      label: _t("prime_strip_boxes_20_pound", "Prime Strip Boxes (20 pound)"),
      value: summaryData?.data?.total_prime_strip_boxes_20_pound ?? 0,
      icon: Box,
    },
    {
      label: _t("seconds_cheek_boxes_20_pound", "Seconds Cheek Boxes (20 pound)"),
      value: summaryData?.data?.total_second_cheeks_boxes_20_pound ?? 0,
      icon: Box,
    },
    {
      label: _t("seconds_strip_boxes_20_pound", "Seconds Strip Boxes (20 pound)"),
      value: summaryData?.data?.total_second_strips_boxes_20_pound ?? 0,
      icon: Box,
    },
    {
      label: _t("tapitas_boxes_20_pound", "Tapitas Boxes (20 pound)"),
      value: summaryData?.data?.total_tapitas_boxes_20_pound ?? 0,
      icon: Box,
    },
    {
      label: _t("pieces_boxes_20_pound", "Pieces Boxes (20 pound)"),
      value: summaryData?.data?.total_pieces_boxes_20_pound ?? 0,
      icon: Box,
    },
  ];

  useEffect(() => {
    const items = packedData?.data?.data;
    setCheeksBoxesPackedData(
      items?.filter((item) => item.type === "cheeks").map((item) => [
        { label: _t("prime_packed_before_lunch", "Prime Packed Before Lunch"), value: `${item.packed_before_lunch_type_one || 0}`, subvalue: _t("ten_pounds", "(10 pounds)") },
        { label: _t("prime_packed_after_lunch", "Prime Packed After Lunch"), value: `${item.packed_after_lunch_type_one || 0}`, subvalue: _t("ten_pounds", "(10 pounds)") },
        { label: _t("prime_packed_before_lunch", "Prime Packed Before Lunch"), value: `${item.packed_before_lunch_type_two || 0}`, subvalue: _t("twenty_pounds", "(20 pounds)") },
        { label: _t("prime_packed_after_lunch", "Prime Packed After Lunch"), value: `${item.packed_after_lunch_type_two || 0}`, subvalue: _t("twenty_pounds", "(20 pounds)") },
        { label: _t("seconds_boxes_before_lunch", "Seconds boxes Before Lunch"), value: item.second_before_lunch_type_two, subvalue: _t("twenty_pounds", "(20 pounds)") },
        { label: _t("seconds_boxes_after_lunch", "Seconds boxes After Lunch"), value: item.second_after_lunch_type_two, subvalue: _t("twenty_pounds", "(20 pounds)") },
        { label: _t("tapita_packed_before_lunch_20_pound", "Tapita packed before lunch"), value: item?.tapita_before_lunch_type_two, subvalue: _t("twenty_pounds", "(20 pounds)") },
        { label: _t("tapita_packed_after_lunch_20_pound", "Tapita packed after lunch"), value: item?.tapita_after_lunch_type_two, subvalue: _t("twenty_pounds", "(20 pounds)") },
        { label: _t("pieces_packed_before_lunch_20_pound", "Pieces packed before lunch"), value: item?.pieces_before_lunch_type_two, subvalue: _t("twenty_pounds", "(20 pounds)") },
        { label: _t("pieces_packed_after_lunch_20_pound", "Pieces packed after lunch"), value: item?.pieces_after_lunch_type_two, subvalue: _t("twenty_pounds", "(20 pounds)") },
        { label: _t("total_cheek_boxes_packed", "Total Cheek Boxes Packed"), value: item.boxes_packed },
        { label: _t("total_cheek_pallets_ready", "Total Cheek Pallets Ready"), value: item.pallets },
      ]) ?? []
    );
    setStripsBoxesPackedData(
      items?.filter((item) => item.type === "strips").map((item) => [
        { label: _t("prime_packed_before_lunch", "Prime Packed Before Lunch"), value: `${item.packed_before_lunch_type_two || 0}`, subvalue: _t("twenty_pounds", "(20 pounds)") },
        { label: _t("prime_packed_after_lunch", "Prime Packed After Lunch"), value: `${item.packed_after_lunch_type_two || 0}`, subvalue: _t("twenty_pounds", "(20 pounds)") },
        { label: _t("seconds_boxes_before_lunch", "Seconds boxes Before Lunch"), value: item.second_before_lunch_type_two, subvalue: _t("twenty_pounds", "(20 pounds)") },
        { label: _t("seconds_boxes_after_lunch", "Seconds boxes After Lunch"), value: item.second_after_lunch_type_two, subvalue: _t("twenty_pounds", "(20 pounds)") },
        { label: _t("pieces_packed_before_lunch_20_pound", "Pieces packed before lunch"), value: item?.pieces_before_lunch_type_two, subvalue: _t("twenty_pounds", "(20 pounds)") },
        { label: _t("pieces_packed_after_lunch_20_pound", "Pieces packed after lunch"), value: item?.pieces_after_lunch_type_two, subvalue: _t("twenty_pounds", "(20 pounds)") },
        { label: _t("total_strip_boxes_packed", "Total Strip Boxes Packed"), value: item.boxes_packed },
        { label: _t("total_strip_pallets_ready", "Total Strip Pallets Ready"), value: item.pallets },
      ]) ?? []
    );
  }, [packedData]);

  const handleCheeksPacked = () => {
    if (!isToday && hasCheeksRecord) return;

    setAddPackingData(true);
    setModalMode("cheeks");
    setSelectedRecord(null);

    if (hasCheeksRecord) {
      setModalMode("editCheeks");
      setSelectedRecord(cheeksRecord);
    }

    setShowModal(true);
  };

  const handleStripsPacked = () => {
    if (!isToday && hasStripsRecord) return;

    setAddPackingData(true);
    setModalMode("strips");
    setSelectedRecord(null);

    if (hasStripsRecord) {
      setModalMode("editStrips");
      setSelectedRecord(stripsRecord);
    }

    setShowModal(true);
  };

  return (
    <div>
      {addPackingData && (
        <RewarkPackedDataModal
          show={showModal}
          setShow={setAddPackingData}
          mode={modalMode}
          record={selectedRecord}
          lot_number_id={lotNumber}
        />
      )}

      <PackingBandModal show={showNightBandModal} setShow={setShowNightBandModal} record={hasNightBandRecord ? nightBandData : null} mode={nightBandMode} onSave={handleNightBandSave} date={date} lotNumber={lotNumber} shift="night" />

      <div className="overview cmn_card mb-3">
        <div className="overview_header d-flex gap-2 align-items-center">
          <h6 className="mb-0 flex-grow-1">
            {_t("rework_log_overview", "Rework Log Overview")}
          </h6>
          <div className="d-flex gap-2 align-items-center">
            <h6 className="mb-0">{_t("batch_code", "Batch Code")}</h6>
            <Button label={`#${lotNumber ?? 0}`} size="small" className="borderRedBtn" />
            <DateSelector setDate={setDate} />
          </div>
        </div>
        <ul className="m-0 grid_card_wrapper">
          {listItems.map((list, index) => (
            <GridCard
              key={index}
              title={list.label}
              icon={list.icon}
              value={list.value}
              day={currentDate === date ? _t("today", "Today") : formatDateAndTime(date, PROJECT_DATE_FORMAT)}
            />
          ))}
        </ul>
        <div className="overview_header d-flex gap-2 align-items-center">
          <h6 className="mb-0 flex-grow-1">
            {_t("rework_packing_overview", "Rework Packing Overview")}
          </h6>
        </div>
        <ul className="m-0 grid_card_wrapper">
         {reworkPackingOverviewItems.map((list, index) => (
            <GridCard
              key={index}
              title={list.label}
              icon={list.icon}
              value={list.value}
              day={currentDate === date ? _t("today", "Today") : formatDateAndTime(date, PROJECT_DATE_FORMAT)}
            />
          ))}
        </ul>
      </div>

      <div className="my-3">
        <h6 className="mb-3">{_t("packed_boxes_log", "Packed Boxes Log")}</h6>
        <div className="gap-2 overviewcard">
          <Row>
            <Col lg={6}>
              <div className="dash_card h-100">
                <div className="d-flex justify-content-between align-items-center dash_header">
                  <div className="packing-card-header-content">
                    <h3 className="mb-0">{_t("cheeks_boxes_packed", "Cheeks Boxes Packed")}</h3>
                    <span className="shift-badge packing-lot-badge">{`${_t('lot_no', 'Lot No.')} #${lotNumber}`}</span>
                  </div>
                  {(isToday || !hasCheeksRecord) && (
                    <Button
                      size="xs"
                      label={hasCheeksRecord ? _t("edit_data", "Edit Data") : _t("add_data", "Add Data")}
                      icon={PlusSvg}
                      onClick={handleCheeksPacked}
                    />
                  )}
                </div>
                <div className="p-3">
                  <Row>
                    {cheeksBoxesPackedData?.length > 0 ? (
                      cheeksBoxesPackedData.map((data, index) => (
                        <Row key={index}>
                          {data.map((item, itemIndex) => (
                            <Col lg={6} key={`${item.label}-${itemIndex}`}>
                              <InfoCard label={item.label} value={item.value} subvalue={item?.subvalue ?? ""} />
                            </Col>
                          ))}
                        </Row>
                      ))
                    ) : (
                      <NoDataFound />
                    )}
                  </Row>
                </div>
              </div>
            </Col>
            <Col lg={6}>
              <div className="dash_card h-100">
                <div className="d-flex justify-content-between align-items-center dash_header">
                  <div className="packing-card-header-content">
                    <h3 className="mb-0">{_t("strips_boxes_packed", "Strips Boxes Packed")}</h3>
                    <span className="shift-badge packing-lot-badge">{`${_t('lot_no', 'Lot No.')} #${lotNumber}`}</span>
                  </div>
                  {(isToday || !hasStripsRecord) && (
                    <Button
                      size="xs"
                      label={hasStripsRecord ? _t("edit_data", "Edit Data") : _t("add_data", "Add Data")}
                      icon={PlusSvg}
                      onClick={handleStripsPacked}
                    />
                  )}
                </div>
                <div className="p-3">
                  <Row>
                    {stripsBoxesPackedData?.length > 0 ? (
                      stripsBoxesPackedData.map((data, index) => (
                        <Row key={index}>
                          {data.map((item, itemIndex) => (
                            <Col lg={6} key={`${item.label}-${itemIndex}`}>
                              <InfoCard label={item.label} value={item.value} subvalue={item?.subvalue ?? ""} />
                            </Col>
                          ))}
                        </Row>
                      ))
                    ) : (
                      <NoDataFound />
                    )}
                  </Row>
                </div>
              </div>
            </Col>
          </Row>
        </div>
      </div>

      {/* Packing Band Log */}
      <div className="packing-band-log-section">
        <h6 className="packing-band-log-title">{_t("packing_band_log", "Packing Band Log")}</h6>
        <div className="gap-2 overviewcard">
          <Row>
            <Col lg={6}>
              <div className="band-card-container">
                <div className="band-card-header">
                  <div className="packing-card-header-content">
                    <h3 className="band-card-title">{_t("night_shift", "Shift 2")}</h3>
                    <span className="shift-badge packing-lot-badge">{`${_t("lot_no", "Lot No.")} #${lotNumber}`}</span>
                  </div>
                  {(isToday || !hasNightBandRecord) && (
                    <Button size="xs" label={hasNightBandRecord ? _t("edit_data", "Edit") : _t("add_data", "Add")} icon={PlusSvg} onClick={handleNightBandAdd} className="band-header-button" />
                  )}
                </div>
                <div className="band-card-body">
                  {nightBandLogData.length > 0 ? nightBandLogData.map((data, index) => (
                    <div key={index} className="mb-2">
                      <div className="band-info-grid">
                        {data.map((item, itemIndex) => (
                          <div key={`${index}-${itemIndex}`} className="band-info-item">
                            <span className="band-info-label">{item.label}</span>
                            <span className="band-info-value">{item.value}</span>
                            {item.subvalue && <span className="band-info-subvalue">{item.subvalue}</span>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )) : <NoDataFound />}
                </div>
              </div>
            </Col>
          </Row>
        </div>
      </div>
    </div>
  );
};

export default ReworkPacking;
